import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Award, Users, Zap } from 'lucide-react';
import { configAPI } from '@/lib/api';

export default function Sobre() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await configAPI.obter();
        setConfig(res.data);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };
    fetchConfig();
  }, []);

  const defaultValues = [
    { icon: Heart, title: 'Paixão pelo esporte', desc: 'Fazer tudo com amor, energia e dedicação ao voleibol.' },
    { icon: Award, title: 'Disciplina e compromisso', desc: 'Ensinar e praticar a responsabilidade e a perseverança dentro e fora das quadras.' },
    { icon: Users, title: 'Respeito', desc: 'Valorizar as diferenças, promovendo um ambiente de inclusão, empatia e trabalho em equipe.' },
    { icon: Zap, title: 'Superação', desc: 'Encorajar nossas atletas a enfrentarem desafios e buscarem constantemente o seu melhor.' },
    { icon: Target, title: 'Desenvolvimento humano', desc: 'Formar não apenas atletas, mas cidadãs conscientes e preparadas para a vida.' },
  ];

  return (
    <div data-testid="sobre-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-yellow-400 font-bold uppercase tracking-widest text-sm">
              Quem Somos
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase mt-4">
              Sobre a <span className="text-yellow-400">ACEC</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Sobre */}
      <section className="py-24 bg-white" data-testid="sobre-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/ai0vmgs1_WhatsApp%20Image%202026-03-05%20at%2008.26.12.jpeg"
                  alt="Time de Vôlei de Quadra"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase">
                Nossa <span className="text-blue-700">História</span>
              </h2>
              <div className="prose prose-lg text-slate-600">
                <p>
                  {config?.sobre_texto || 
                    "Somos atletas apaixonados pelo vôlei, com anos de experiência dentro e fora das quadras. Sabemos, na prática, como esse esporte é capaz de transformar vidas — não apenas no aspecto físico, mas também no social, emocional e pessoal."}
                </p>
                <p>
                  Percebemos a carência de oportunidades para a prática do voleibol em nossa cidade e decidimos fazer a diferença. Nosso objetivo é oferecer aulas de qualidade, formar novos talentos e mostrar o poder que o esporte tem de impactar positivamente a vida de crianças, adolescentes e adultos.
                </p>
                <p>
                  Mais do que ensinar fundamentos técnicos, queremos inspirar, integrar e fortalecer a comunidade por meio do esporte que amamos.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Missão, Visão */}
      <section className="py-24 section-gradient-blue" data-testid="missao-visao">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Missão */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-10 card-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-blue-700" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-4">
                Nossa Missão
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {config?.missao || 
                  "Inspirar e transformar vidas por meio do voleibol, oferecendo acesso a treinamentos de qualidade, promovendo a inclusão social e incentivando o desenvolvimento pessoal, esportivo e cidadão das nossas atletas."}
              </p>
            </motion.div>

            {/* Visão */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-10 card-shadow"
            >
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-4">
                Nossa Visão
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {config?.visao || 
                  "Levar o nome da ACEC Feminino além das quadras nacionais e nos tornar referências, formando atletas e cidadãs reconhecidas pelo espírito esportivo e pelos valores humanos que representam."}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-24 bg-white" data-testid="valores-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-yellow-500 font-bold uppercase tracking-widest text-sm">
              Princípios
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 uppercase mt-2">
              Nossos <span className="text-blue-700">Valores</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {defaultValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300"
              >
                <value.icon className="w-10 h-10 text-blue-700 mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
